import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Typography, Paper, Alert, CircularProgress, List, ListItem, ListItemText, ListItemButton, Divider } from '@mui/material'
import { QrCode2, CameraAlt, Search, ArrowBack, ReportProblem, Build, Visibility, DirectionsCar } from '@mui/icons-material'
import { PageHeader, Button, Card } from '@components/common'
import { Html5Qrcode } from 'html5-qrcode'
import { toast } from 'react-toastify'
import api from '@services/api'
import { formatDate, formatDateTime } from '@utils/dateUtils'
import FaultReportForm from '@components/assets/FaultReportForm'
import WorkOrderForm from '@components/maintenance/WorkOrderForm'

const AssetScanner = () => {
  const navigate = useNavigate()
  const [scanning, setScanning] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [scannedAsset, setScannedAsset] = useState(null)
  const [scannedVehicle, setScannedVehicle] = useState(null)
  const [recentScans, setRecentScans] = useState([])
  const [cameraPermission, setCameraPermission] = useState(null)
  const [faultFormOpen, setFaultFormOpen] = useState(false)
  const [woFormOpen, setWoFormOpen] = useState(false)
  const scannerRef = useRef(null)
  const html5QrCodeRef = useRef(null)

  useEffect(() => {
    // Load recent scans from localStorage
    const saved = localStorage.getItem('recentAssetScans')
    if (saved) {
      try {
        setRecentScans(JSON.parse(saved))
      } catch (e) {
        console.error('Error loading recent scans:', e)
      }
    }

    // Cleanup on unmount
    return () => {
      stopScanner()
    }
  }, [])

  const startScanner = async () => {
    setError(null)
    setScannedAsset(null)
    setScannedVehicle(null)
    setCameraPermission(null)

    try {
      // Initialize scanner first
      if (!html5QrCodeRef.current) {
        html5QrCodeRef.current = new Html5Qrcode('qr-reader')
      }

      setScanning(true)

      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
      }

      // Let html5-qrcode handle camera permissions directly
      await html5QrCodeRef.current.start(
        { facingMode: 'environment' },
        config,
        onScanSuccess,
        onScanFailure
      )

      setCameraPermission(true)
    } catch (err) {
      console.error('Scanner error:', err)
      setScanning(false)

      if (err.name === 'NotAllowedError' || err.message?.includes('Permission')) {
        setCameraPermission(false)
        setError('Camera permission denied. Please allow camera access and try again.')
      } else if (err.name === 'NotFoundError' || err.message?.includes('No camera')) {
        setError('No camera found. Please connect a camera and try again.')
      } else if (err.name === 'NotReadableError' || err.message?.includes('in use')) {
        setError('Camera is in use by another application. Please close other apps using the camera.')
      } else if (err.message?.includes('SSL') || err.message?.includes('https')) {
        setError('Camera access requires HTTPS. Please access this page via HTTPS.')
      } else {
        setError(`Unable to access camera: ${err.message || 'Unknown error'}`)
      }
    }
  }

  const stopScanner = async () => {
    if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
      try {
        await html5QrCodeRef.current.stop()
      } catch (err) {
        console.error('Error stopping scanner:', err)
      }
    }
    setScanning(false)
  }

  const onScanSuccess = async (decodedText) => {
    // Stop scanner after successful scan
    await stopScanner()

    setLoading(true)
    try {
      let response

      // Handle CMMS-VEH: format (vehicle QR generated by backend)
      if (decodedText.startsWith('CMMS-VEH:')) {
        try {
          response = await api.get('/assets/vehicles/by_qr/', {
            params: { code: decodedText }
          })
          setScannedVehicle(response.data)
          toast.success(`Found vehicle: ${response.data.name}`)
        } catch (err) {
          if (err.response?.status === 404) {
            setError(`Vehicle not found for QR code: ${decodedText}`)
            toast.error('Vehicle not found')
          } else {
            throw err
          }
        }
        return
      }

      // Handle CMMS-ASSET: format (generated by backend)
      if (decodedText.startsWith('CMMS-ASSET:')) {
        try {
          response = await api.get('/assets/assets/by_qr/', {
            params: { code: decodedText }
          })
          setScannedAsset(response.data)
          addToRecentScans(response.data)
          toast.success(`Found asset: ${response.data.name}`)
          return
        } catch (err) {
          if (err.response?.status === 404) {
            setError(`Asset not found for QR code: ${decodedText}`)
            toast.error('Asset not found')
          } else {
            throw err
          }
          return
        }
      }

      // Try to parse the QR code - could be asset tag, ID, or URL
      let assetIdentifier = decodedText

      // If it's a URL, extract the asset ID or tag
      if (decodedText.includes('/assets/')) {
        const match = decodedText.match(/\/assets\/(\d+)/)
        if (match) {
          assetIdentifier = match[1]
        }
      }

      // Search for asset by tag or ID
      if (!isNaN(assetIdentifier)) {
        // It's a number, try as ID first
        try {
          response = await api.get(`/assets/assets/${assetIdentifier}/`)
          setScannedAsset(response.data)
          addToRecentScans(response.data)
          toast.success(`Found asset: ${response.data.name}`)
          return
        } catch (e) {
          // Not found by ID, try as asset tag
        }
      }

      // Search by asset tag
      response = await api.get('/assets/assets/', {
        params: { search: assetIdentifier }
      })

      const results = response.data.results || response.data
      if (results.length === 1) {
        setScannedAsset(results[0])
        addToRecentScans(results[0])
        toast.success(`Found asset: ${results[0].name}`)
      } else if (results.length > 1) {
        // Multiple matches, show the first one but notify user
        setScannedAsset(results[0])
        addToRecentScans(results[0])
        toast.info(`Found ${results.length} assets, showing first match`)
      } else {
        setError(`No asset found for: ${decodedText}`)
        toast.error('Asset not found')
      }
    } catch (err) {
      console.error('Error looking up asset:', err)
      setError(`Could not find asset: ${decodedText}`)
      toast.error('Error looking up asset')
    } finally {
      setLoading(false)
    }
  }

  const onScanFailure = (error) => {
    // Ignore scan failures (no QR code detected)
    // console.log('Scan error:', error)
  }

  const addToRecentScans = (asset) => {
    const newScans = [
      { id: asset.id, name: asset.name, asset_tag: asset.asset_tag, scannedAt: new Date().toISOString() },
      ...recentScans.filter(s => s.id !== asset.id)
    ].slice(0, 10) // Keep only last 10

    setRecentScans(newScans)
    localStorage.setItem('recentAssetScans', JSON.stringify(newScans))
  }

  const viewAsset = (assetId) => {
    navigate(`/assets/${assetId}`)
  }

  return (
    <Box>
      <PageHeader
        title="Scan QR Code"
        subtitle="Scan an asset or vehicle QR code to view details or start a service"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Assets', path: '/assets' },
          { label: 'Scan QR' },
        ]}
        actions={
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/assets')}
          >
            Back to Assets
          </Button>
        }
      />

      <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
        {/* Scanner Section */}
        <Box flex={2}>
          <Card>
            <Box textAlign="center">
              {/* Scanner viewport */}
              <Box
                id="qr-reader"
                ref={scannerRef}
                sx={{
                  width: '100%',
                  maxWidth: 400,
                  mx: 'auto',
                  mb: 2,
                  minHeight: scanning ? 300 : 0,
                  '& video': {
                    borderRadius: 2,
                  }
                }}
              />

              {!scanning && !scannedAsset && !loading && (
                <Box py={4}>
                  <QrCode2 sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Ready to Scan
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={3}>
                    Position an asset or vehicle QR code within the camera frame
                  </Typography>
                  <Button
                    size="large"
                    startIcon={<CameraAlt />}
                    onClick={startScanner}
                  >
                    Start Scanner
                  </Button>
                </Box>
              )}

              {scanning && (
                <Box>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Point camera at an asset or vehicle QR code...
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={stopScanner}
                  >
                    Stop Scanner
                  </Button>
                </Box>
              )}

              {loading && (
                <Box py={4}>
                  <CircularProgress size={48} />
                  <Typography variant="body2" color="text.secondary" mt={2}>
                    Looking up asset...
                  </Typography>
                </Box>
              )}

              {error && (
                <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>
                  {error}
                  <Box mt={1}>
                    <Button size="small" onClick={startScanner}>
                      Try Again
                    </Button>
                  </Box>
                </Alert>
              )}

              {cameraPermission === false && (
                <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    Camera access is required for QR scanning.
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    To enable camera access:
                  </Typography>
                  <Box component="ol" sx={{ pl: 2, my: 1, '& li': { mb: 0.5 } }}>
                    <li>Click the camera/lock icon in your browser's address bar</li>
                    <li>Find "Camera" in the permissions list</li>
                    <li>Change it from "Block" to "Allow"</li>
                    <li>Click the button below to try again</li>
                  </Box>
                  <Box mt={2} display="flex" gap={1}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => {
                        setCameraPermission(null)
                        setError(null)
                        startScanner()
                      }}
                    >
                      Request Camera Access
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => window.location.reload()}
                    >
                      Refresh Page
                    </Button>
                  </Box>
                </Alert>
              )}
            </Box>
          </Card>

          {/* Scanned Asset Result */}
          {scannedAsset && (
            <Card title="Scanned Asset" sx={{ mt: 3 }}>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                  <Typography variant="h6">{scannedAsset.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tag: {scannedAsset.asset_tag}
                  </Typography>
                </Box>

                <Divider />

                <Box display="flex" flexWrap="wrap" gap={3}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Category</Typography>
                    <Typography variant="body2">{scannedAsset.category_name || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Location</Typography>
                    <Typography variant="body2">{scannedAsset.location_name || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {scannedAsset.status || '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Serial Number</Typography>
                    <Typography variant="body2">{scannedAsset.serial_number || '-'}</Typography>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    What would you like to do?
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    <Button
                      startIcon={<ReportProblem />}
                      onClick={() => setFaultFormOpen(true)}
                    >
                      Report Fault
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Build />}
                      onClick={() => setWoFormOpen(true)}
                    >
                      Create Work Order
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Visibility />}
                      onClick={() => viewAsset(scannedAsset.id)}
                    >
                      View Details
                    </Button>
                  </Box>
                </Box>

                <Divider />

                <Box display="flex" gap={1}>
                  <Button
                    variant="text"
                    startIcon={<CameraAlt />}
                    onClick={() => {
                      setScannedAsset(null)
                      startScanner()
                    }}
                  >
                    Scan Another
                  </Button>
                </Box>
              </Box>
            </Card>
          )}

          {/* Scanned Vehicle Result */}
          {scannedVehicle && (
            <Card title="Scanned Vehicle" sx={{ mt: 3 }}>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <DirectionsCar color="primary" />
                  <Box>
                    <Typography variant="h6">{scannedVehicle.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {scannedVehicle.vehicle_number}
                      {scannedVehicle.registration_number ? ` | ${scannedVehicle.registration_number}` : ''}
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                <Box display="flex" flexWrap="wrap" gap={3}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Type</Typography>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {scannedVehicle.vehicle_type?.replace('_', ' ') || '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Section/Unit</Typography>
                    <Typography variant="body2">{scannedVehicle.department_name || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {scannedVehicle.status?.replace('_', ' ') || '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Mileage</Typography>
                    <Typography variant="body2">
                      {scannedVehicle.current_mileage != null ? `${Number(scannedVehicle.current_mileage).toLocaleString()} km` : '-'}
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    What would you like to do?
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    <Button
                      startIcon={<ReportProblem />}
                      onClick={() => setFaultFormOpen(true)}
                    >
                      Report Fault
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Build />}
                      onClick={() => setWoFormOpen(true)}
                    >
                      Create Work Order
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Visibility />}
                      onClick={() => navigate(`/assets/vehicles?vehicle=${scannedVehicle.id}`)}
                    >
                      View Details
                    </Button>
                  </Box>
                </Box>

                <Divider />

                <Box display="flex" gap={1}>
                  <Button
                    variant="text"
                    startIcon={<CameraAlt />}
                    onClick={() => {
                      setScannedVehicle(null)
                      startScanner()
                    }}
                  >
                    Scan Another
                  </Button>
                </Box>
              </Box>
            </Card>
          )}
        </Box>

        {/* Recent Scans Section */}
        <Box flex={1}>
          <Card title="Recent Scans">
            {recentScans.length === 0 ? (
              <Box textAlign="center" py={3}>
                <Search sx={{ fontSize: 40, color: 'grey.400' }} />
                <Typography variant="body2" color="text.secondary" mt={1}>
                  No recent scans
                </Typography>
              </Box>
            ) : (
              <List dense disablePadding>
                {recentScans.map((scan, index) => (
                  <ListItemButton
                    key={scan.id}
                    onClick={() => viewAsset(scan.id)}
                    sx={{ borderRadius: 1, mb: 0.5 }}
                  >
                    <ListItemText
                      primary={scan.name}
                      secondary={
                        <>
                          <Typography variant="caption" component="span">
                            {scan.asset_tag}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(scan.scannedAt)}
                          </Typography>
                        </>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            )}

            {recentScans.length > 0 && (
              <Button
                size="small"
                color="error"
                fullWidth
                sx={{ mt: 2 }}
                onClick={() => {
                  setRecentScans([])
                  localStorage.removeItem('recentAssetScans')
                }}
              >
                Clear History
              </Button>
            )}
          </Card>

          {/* Tips Card */}
          <Card title="Scanning Tips" sx={{ mt: 3 }}>
            <List dense disablePadding>
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemText
                  primary="Good lighting"
                  secondary="Ensure adequate lighting for best results"
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemText
                  primary="Hold steady"
                  secondary="Keep the camera steady while scanning"
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
              <ListItem disablePadding>
                <ListItemText
                  primary="Center the code"
                  secondary="Position QR code in the center of the frame"
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            </List>
          </Card>
        </Box>
      </Box>

      {/* Fault Report Dialog (asset or vehicle target) */}
      <FaultReportForm
        open={faultFormOpen}
        onClose={() => setFaultFormOpen(false)}
        asset={scannedVehicle ? null : scannedAsset}
        vehicle={scannedVehicle}
        onSuccess={() => setFaultFormOpen(false)}
      />

      {/* Work Order Dialog (asset or vehicle target) */}
      <WorkOrderForm
        open={woFormOpen}
        onClose={() => setWoFormOpen(false)}
        defaultAssetId={scannedVehicle ? '' : scannedAsset?.id}
        vehicle={scannedVehicle}
        onSuccess={() => {
          setWoFormOpen(false)
          toast.success('Work order created')
        }}
      />
    </Box>
  )
}

export default AssetScanner
